import { FC, useRef, MouseEvent } from 'react';
import { useRecordContext, ListContextProvider, ResourceContextProvider, useEditContext, ReduxState, Identifier, toggleListItemExpand, ListControllerProps } from 'ra-core';
import { Card, CardHeader, Box, Typography, Stack, Tooltip, CircularProgress } from '@mui/material';
import { Datagrid, ExpandRowButton } from '@/components/list/datagrid';
import { FunctionField, TextField as RaTextField } from '@/components/field';
import { useParams } from 'react-router-dom';
import { RecPatch } from '@/dataProvider/tools';
import { AtopFuncArea, SearchField } from '@/components/list/datagridFunctionWidgets';
import EditIcon from '@mui/icons-material/Edit';
import IconButton from '@mui/material/IconButton';
import { useDispatch, useSelector } from 'react-redux';
import { push } from 'connected-react-router';
import { decodeHashid, genUrlWithHashid } from '@/utils/hashid';
import Edit from '@/controller/Edit';
import { Content } from '@/components/content/Content';
import { useDefaultSavedHandlers } from '@/controller/defaultSavedHandlers';
import RowExpandSchemaSemanticEdit from '@/components/governance/RowExpandSchemaSemanticEdit';
import RowExpandTableSemanticEdit from '@/components/governance/RowExpandTableSemanticEdit';
import { useResourceRegister } from '@/utils/resourceRegister';
import useLocalListController from '@/components/list/useLocalListController';
import { DatabaseResponseDto, SchemaResponseDto, TableSchemaResponseDto } from '@/gen/api/governance';
import DatabaseSemanticEditCard from '@/components/governance/DatabaseSemanticEditCard';
import { OdResourceOption, OdResourcePageProps } from '@/types/resource';
import { Pagination } from '@/components/list/pagination';
import { createPortal } from 'react-dom';
import { datagridClasses } from '@/components/list/datagrid/useDatagridStyles';
import { waitMilis } from '@/utils/asyncUtils';



const dbSemanticRes = 'governance/database-semantic'
const schemaSemanticRes = 'governance/schema-semantic'
const tableSemanticRes = 'governance/table-semantic'

const DatabaseSemanticDetail : FC<OdResourcePageProps> = ({
  options
}) => {
  useResourceRegister({ name: schemaSemanticRes })
  useResourceRegister({ name: tableSemanticRes })

  const { id: databaseHashId } = useParams<{ id: string }>();
  // Extract database ID from hashid (for now, use a simple mapping)
  const databaseId = decodeHashid(databaseHashId || '')
  const handler = useDefaultSavedHandlers("Save Database description")

  return (
    <Edit id={databaseId} resource={dbSemanticRes} basePath='' mutationMode='pessimistic' {...handler}>
      <DatabaseSemanticDetailBody databaseId={databaseId} options={options} />
    </Edit>
  )
}

const DatabaseSemanticDetailBody : FC<{ databaseId: number, options?: OdResourceOption }> = ({
  databaseId, options,
}) => {
  const database : DatabaseResponseDto = useRecordContext()
  const { refetch } = useEditContext()
  const dispatch = useDispatch();

  const navigateToFieldEdit = (tableId: number, tableName: string) => {
    const dbHashId = genUrlWithHashid("$0", databaseId, database.name ?? '');
    const tableHashId = genUrlWithHashid("$0", tableId, tableName);
    // Navigate to table fields edit page
    dispatch(push(`/governance/database-semantic/${dbHashId}/table-fields/${tableHashId}/edit`));
  };

  // Get schemas data
  const schemasListCtrl = useLocalListController({
    resource: schemaSemanticRes, basePath: '',
    filter: { databaseId },
  }, { sorters: scSorters });

  // Get tables data
  const tablesListCtrl = useLocalListController({
    resource: tableSemanticRes, basePath: '',
    filter: { databaseId },
  }, { sorters: tbSorters });

  const { setSRef, setTRef,
    SchemasExpandAllPortal,
    TablesExpandAllPortal
  } = useExpandAllHelpers({ schemasListCtrl, tablesListCtrl })

  if (!database) {
    return (
      <Stack direction="row" justifyContent="center" marginTop={4}>
        <CircularProgress />
      </Stack>
    );
  }

  return (
    <Content
      pageName="Semantic Data"
      resourceName="Semantic Data"
      breadcrumbs={options?.breadcrumbs}
    >
      {/* Database Information Card */}
      <Box sx={{ mb: 3 }}>
        <DatabaseSemanticEditCard database={database} refetch={refetch} />
      </Box>

      {/* Schemas Section */}
      <ResourceContextProvider value={schemaSemanticRes}>
        <ListContextProvider value={schemasListCtrl}>
          <Card sx={{ mb: 3 }}>
            <CardHeader title={
              <Stack direction="row" alignItems="center" >
                <Typography variant="h6" component="span">
                  Schemas
                </Typography>

                <AtopFuncArea boxSx={{ p: 0, ml: '53px' }}>
                  <SearchField sourceKey="name" sx={{ width: 280 }}
                    TextFieldProps={{ placeholder: 'Search schemas' }}
                  />
                </AtopFuncArea>
              </Stack>
            } />
            {schemasListCtrl.data && schemasListCtrl.ids.length > 0 ? (
              <>
                <Datagrid ref={setSRef}
                  sx={{ 
                    '& th' : { pb: 1 },
                    '& td' : { py: 1 },
                    '& th.RaDatagrid-expandHeader': { textAlign: 'center' },
                    '& td.column-name': { width: 80 },
                    '& td.column-description': { width: 720 },
                    '& td.column-comment': { width: 400 },
                  }}
                  expand={<RowExpandSchemaSemanticEdit />}
                  rowClick="expand"
                  {...schemasListCtrl}
                >
                  <RaTextField label="Schema Name" source="name" sortable={false} className='name' />
                  <RaTextField label="Description" source="description" sortable={false} className='description' />
                  <RaTextField label="Comment" source="comment" sortable={false} className='comment' />
                </Datagrid>
                <Pagination {...(schemasListCtrl as any)} />
                <SchemasExpandAllPortal />
              </>
            ) : (
              schemasListCtrl.loading &&
              <Stack direction="row" justifyContent="center" marginTop={4}>
                <CircularProgress />
              </Stack>
            )}
          </Card>
        </ListContextProvider>
      </ResourceContextProvider>

      {/* Tables Section */}
      <ResourceContextProvider value={tableSemanticRes}>
        <ListContextProvider value={tablesListCtrl}>
          <Card>
            <CardHeader title={
              <Stack direction="row" alignItems="center" >
                <Typography variant="h6" component="span">
                  Tables
                </Typography>

                <AtopFuncArea boxSx={{ p: 0, ml: '73px' }}>
                  <SearchField sourceKey="name" sx={{ width: 280 }}
                    TextFieldProps={{ placeholder: 'Search tables' }}
                  />
                </AtopFuncArea>
              </Stack>
            } />
            {tablesListCtrl.data && tablesListCtrl.ids.length > 0 ? (
              <>
                <Datagrid ref={setTRef}
                  sx={{ 
                    '& th' : { pb: 1 },
                    '& td' : { py: 1 },
                    '& th.RaDatagrid-expandHeader': { textAlign: 'center' },
                    '& td.column-name': { width: 80 },
                    '& td.column-description': { width: 660 },
                    '& td.column-comment': { width: 400 },
                    '& td.column-action': { width: 60 },
                  }}
                  expand={<RowExpandTableSemanticEdit />}
                  rowClick="expand"
                  {...tablesListCtrl}
                >
                  <FunctionField label="Table" sortable={false} className='name'
                    render={(record?: TableSchemaResponseDto&RecPatch) => {
                      return (
                        record &&
                        <>
                          <Typography variant="caption" color="textSecondary">
                            {record.schema}.
                          </Typography>
                          <Typography variant="body2" color="textPrimary">
                            { record.name }
                          </Typography>
                        </>
                      )
                    }} 
                  />
                  <RaTextField label="Description" source="description" sortable={false} className='description' />
                  <RaTextField label="Comment" source="comment" sortable={false} className='comment' />
                  <FunctionField label="Actions" sortable={false} className='action'
                    render={(record?: TableSchemaResponseDto&RecPatch) => {
                      return (
                        record &&
                        <Tooltip title="Edit Semantic Data">
                          <IconButton color="info" size="small"
                            aria-label="Edit Semantic Data"
                            onClick={() => navigateToFieldEdit(record.tableId!, record.name!)}
                          >
                            <EditIcon fontSize='small'/>
                          </IconButton>
                        </Tooltip>
                      );
                    }}
                  />
                </Datagrid>
                <Pagination {...(tablesListCtrl as any)} />
                <TablesExpandAllPortal />
              </>
            ) : (
              tablesListCtrl.loading &&
              <Stack direction="row" justifyContent="center" marginTop={4}>
                <CircularProgress />
              </Stack>
            )}
          </Card>
        </ListContextProvider>
      </ResourceContextProvider>
    </Content>
  );
};

export default DatabaseSemanticDetail;


const useExpandAllHelpers = ({
  schemasListCtrl, tablesListCtrl,
}: {
  schemasListCtrl: ListControllerProps<any>,
  tablesListCtrl: ListControllerProps<any>,
}) => {
  const th_s_ref = useRef<HTMLElement|null>(null)
  const th_t_ref = useRef<HTMLElement|null>(null)
  const setRef = (th_ref: React.MutableRefObject<HTMLElement|null>) => (table: HTMLTableElement) => {
    th_ref.current = table?.querySelector('th.MuiTableCell-head.RaDatagrid-expandHeader')
  }
  const dispatch = useDispatch()
  type ExpandInfo = {
    expanded: Identifier[];
    anySchemasExpanded: boolean;
  };
  type ExpandInfoBag = {
    [schemaSemanticRes]: ExpandInfo;
    [tableSemanticRes]: ExpandInfo;
  }
  const expandedInfo = useSelector<ReduxState, ExpandInfoBag>(store => {
    const s_expanded = (store.admin.resources[schemaSemanticRes]?.list.expanded ?? []) as Identifier[]
    const t_expanded = (store.admin.resources[tableSemanticRes]?.list.expanded ?? []) as Identifier[]
    return {
      [schemaSemanticRes]: {
        expanded: s_expanded,
        anySchemasExpanded: (schemasListCtrl.ids ?? []).some(id => s_expanded.includes(id)),
      },
      [tableSemanticRes]: {
        expanded: t_expanded,
        anySchemasExpanded: (tablesListCtrl.ids ?? []).some(id => t_expanded.includes(id)),
      },
    }
  })
  const toggleExpandAll = (res: keyof ExpandInfoBag, ids: Identifier[]|undefined) => async (e: MouseEvent<HTMLButtonElement>) => {
    if (expandedInfo[res].anySchemasExpanded) {
      for (const id of (ids ?? []).filter(id => expandedInfo[res].expanded.includes(id))) {
        dispatch(toggleListItemExpand(res, id))
      }
    } else {
      const btn: HTMLButtonElement = e.target as any
      const th = btn.closest('th')
      for (const id of (ids ?? [])) {
        dispatch(toggleListItemExpand(res, id))
      }
      await waitMilis(20)
      th?.scrollIntoView({ block: 'start', behavior: 'instant' });
    }
  }
  return {
    SchemasExpandAllPortal: () => {
      return (!th_s_ref.current ? null :
        createPortal(
          <ExpandRowButton classes={datagridClasses} 
            expanded={expandedInfo[schemaSemanticRes].anySchemasExpanded} 
            onClick={toggleExpandAll(schemaSemanticRes, schemasListCtrl.ids)} 
          />,
          th_s_ref.current
        )
      )
    },
    
    TablesExpandAllPortal: () => {
      return (!th_t_ref.current ? null :
        createPortal(
          <ExpandRowButton classes={datagridClasses} 
            expanded={expandedInfo[tableSemanticRes].anySchemasExpanded} 
            onClick={toggleExpandAll(tableSemanticRes, tablesListCtrl.ids)} 
          />,
          th_t_ref.current
        )
      )
    },

    setSRef: setRef(th_s_ref),
    setTRef: setRef(th_t_ref),
  }
}

const scSorters = {
  id: (a: SchemaResponseDto, b: SchemaResponseDto) => (a.name ?? '') < (b.name ?? '') ? -1 : 1,
} as any
const tbSorters = {
  id: (a: TableSchemaResponseDto, b: TableSchemaResponseDto) => 
    (a.schema ?? '') === (b.schema ?? '') 
      ? ((a.name ?? '') < (b.name ?? '') ? -1 : 1)
      : (a.schema ?? '') < (b.schema ?? '') ? -1 : 1,
} as any
